import { useState, useEffect, useCallback } from "react";

import {
  ArrowUpDown,
  ChevronDown,
  X,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { useNotification } from "../context/NotificationProvider";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import coin from "../assets/images/placeholder/crypto-colour.png";
import { useTradeRate } from "../hooks/useTradeRate";
import { getAssetPairs } from "../services/tradingDataService";
import { truncateDecimals } from "../utils/numberFormatter";
export default function TradePairSaleForex({
  selectedSaleAsset,
  onSelect,
  onChange,
  setParentRate,
  typeOfTrade,
  onTarget,
  onFetchRate,
  awaitingRateSecondary,
  awaitingSaleRate,
  setAwaitingSaleRate,
  onReject,
  onAccept,
  setIsBaseInputParent,
  setAmount,
  amount,
  onTargetCancel,
  isPostTrade,
  currentPairs,
}) {
  const [isBaseInput, setIsBaseInput] = useState(false);
  const [tragetAmount, setTragetAmount] = useState(null);
  const [tradePair, setTradePair] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  const [pairInfo, setPairInfo] = useState(null);
  const [rate, setRate] = useState(null);
  const [open, setOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingRate, setLoadingRate] = useState(true);
  const [highTarget, setHighTarget] = useState(false);
  const [flag, setFlag] = useState(false);
  const [flagMinimum, setFlagMinimum] = useState(false);
  const [awaitingRate, setAwaitingRate] = useState(awaitingRateSecondary);
  const [convertedValue, setConvertedValue] = useState(0);
  const [targetAmountHolder, setTargetAmountHolder] = useState(null);
  const { showPersistentMessage } = useNotification();
  const { data: tradeRate } = useTradeRate(
    selectedSaleAsset?.assetId,
    "sell",
    selectedPair?.pair,
    isPostTrade
  );

  useEffect(() => {
    if (!currentPairs) return;
    const fetchPairs = async () => {
      try {
        currentPairs.forEach((pair) => {
          if (pair.asset === "zar") {
            setSelectedPair(pair);
            if (onSelect) {
              onSelect(pair);
            }
          }
        });
        setTradePair(currentPairs);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchPairs();
  }, [currentPairs, onSelect]);

  useEffect(() => {
    if (!tradePair || tradePair.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [tradePair]);

  useEffect(() => {
    if (!rate || rate.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [rate]);

  useEffect(() => {
    if (!selectedPair || !selectedSaleAsset) return;
    setLoadingRate(true);
    setAwaitingRate(true);
    setAwaitingSaleRate(true);
  }, [selectedPair, selectedSaleAsset, setAwaitingSaleRate]);

  useEffect(() => {
    if (!selectedPair || !selectedSaleAsset || !tradeRate) return;

    const fetchRate = async () => {
      try {
        setRate(tradeRate);
        setParentRate(tradeRate);
      } catch (err) {
        showPersistentMessage(
          "error",
          "Trading rates are not available at the moment please try again later"
        );
      } finally {
        setLoading(false);
        setLoadingRate(false);
        setAwaitingRate(false);
        setAwaitingSaleRate(false);
        if (onFetchRate) {
          onFetchRate();
        }
      }
    };
    fetchRate();
  }, [
    tradeRate,
    selectedPair,
    selectedSaleAsset,
    setAwaitingSaleRate,
    setParentRate,
    showPersistentMessage,
    onFetchRate,
  ]);

  useEffect(() => {
    if (!selectedSaleAsset?.assetId) return;

    const fetchPairs = async () => {
      try {
        const data = await getAssetPairs(selectedSaleAsset.assetId);
        setPairInfo(data || []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error("Error fetching pair info:", errorMessage);
        setPairInfo([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchPairs();
  }, [selectedSaleAsset]);

  const handleSelect = useCallback(
    (asset) => {
      setSelectedPair(asset);
      setOpen(false);
      if (onSelect) {
        onSelect(asset);
      }
    },
    [onSelect]
  );

  const takeInOutAmount = useCallback(
    (newAmount, convertedValue) => {
      if (onChange) {
        onChange(newAmount, convertedValue);
      }
    },
    [onChange]
  );

  const takeOutInAmount = useCallback(
    (convertedValue, newAmount) => {
      if (onChange) {
        onChange(convertedValue, newAmount);
      }
    },
    [onChange]
  );

  const onTextChange = (event) => {
    const rawValue = event.target.value;
    if (/^\d*\.?\d*$/.test(rawValue)) {
      setAmount(event.target.value);
      const newAmount = parseFloat(event.target.value);
      if (pairInfo && selectedPair?.pair) {
        const matchingPairInfo = pairInfo.find(
          (info) => info.pair === selectedPair.pair
        );

        if (matchingPairInfo && matchingPairInfo.minSellAmountTail) {
          const minSellRaw =
            typeof matchingPairInfo.minSellAmountTail === "number"
              ? matchingPairInfo.minSellAmountTail
              : parseFloat(
                  matchingPairInfo.minSellAmountTail.$numberDecimal || "0"
                );

          const minSell = isBaseInput
            ? minSellRaw / (rate?.rate ?? 1)
            : minSellRaw;

          if (newAmount >= minSell) {
            setAmount(rawValue);
            if (onAccept) {
              onAccept();
            }
            setFlagMinimum(false);
          } else {
            if (onReject) {
              onReject();
            }
            setFlagMinimum(true);
          }
        }
      }
    }
  };

  useEffect(() => {
    const number = parseFloat(amount || 0);

    if (isNaN(number) || number === 0) {
      setConvertedValue(0);
      return;
    }
    if (isBaseInput) {
      if (!tragetAmount) {
        const convertedValue = number * rate?.rate;
        const asset = selectedSaleAsset?.asset?.toLowerCase();
        const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
        const formattedValue = isTwoDecimalCurrency
          ? parseFloat(convertedValue.toFixed(2))
          : convertedValue;
        setConvertedValue(formattedValue);
        takeInOutAmount(formattedValue, number);
        if (number > selectedSaleAsset?.availableAmount) {
          if (!isPostTrade) {
            setFlag(true);
            if (onReject) {
              onReject();
            }
          }
          if (isPostTrade) {
            setFlag(false);
            if (onAccept) {
              onAccept();
            }
          }
        } else {
          setFlag(false);
          if (onAccept) {
            onAccept();
          }
        }
      }
      if (tragetAmount) {
        const convertedValue = number * tragetAmount;
        const asset = selectedSaleAsset?.asset?.toLowerCase();
        const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
        const formattedValue = isTwoDecimalCurrency
          ? parseFloat(convertedValue.toFixed(2))
          : convertedValue;
        setConvertedValue(formattedValue);
        takeInOutAmount(formattedValue, number);
      }
    } else {
      if (!tragetAmount) {
        const convertedValue = number / rate?.rate;
        const formattedValue = parseFloat(convertedValue.toFixed(2));
        setConvertedValue(formattedValue);
        takeOutInAmount(number, formattedValue);
        if (formattedValue > selectedSaleAsset?.availableAmount) {
          if (!isPostTrade) {
            setFlag(true);
            if (onReject) {
              onReject();
            }
          }
          if (isPostTrade) {
            setFlag(false);
            if (onAccept) {
              onAccept();
            }
          }
        } else {
          if (onAccept) {
            onAccept();
          }
          setFlag(false);
        }
      }
      if (tragetAmount) {
        const convertedValue = number / tragetAmount;
        const formattedValue = parseFloat(convertedValue.toFixed(2));
        setConvertedValue(formattedValue);
        takeOutInAmount(number, formattedValue);
      }
    }
  }, [
    amount,
    rate,
    isBaseInput,
    isPostTrade,
    tragetAmount,
    selectedSaleAsset?.availableAmount,
    selectedSaleAsset?.asset,
    onAccept,
    onReject,
    takeInOutAmount,
    takeOutInAmount,
  ]);

  const validateAmount = (newAmount, rawValue, inputType) => {
    if (pairInfo && selectedPair?.pair) {
      const currentInputType =
        inputType !== undefined ? inputType : isBaseInput;
      const matchingPairInfo = pairInfo.find(
        (info) => info.pair === selectedPair.pair
      );

      if (matchingPairInfo && matchingPairInfo.minSellAmountTail) {
        const minSellRaw =
          typeof matchingPairInfo.minSellAmountTail === "number"
            ? matchingPairInfo.minSellAmountTail
            : parseFloat(
                matchingPairInfo.minSellAmountTail.$numberDecimal || "0"
              );

        const minSell = currentInputType
          ? minSellRaw / (rate?.rate ?? 1)
          : minSellRaw;

        if (newAmount >= minSell) {
          if (onAccept) {
            onAccept();
          }
          setFlagMinimum(false);
        } else {
          if (onReject) {
            onReject();
          }
          setFlagMinimum(true);
        }
      }
    }
  };

  const maxQuoteAmount = () => {
    if (!selectedSaleAsset?.availableAmount || !rate?.rate) {
      return;
    }

    let newAmount;

    if (isBaseInput) {
      newAmount = selectedSaleAsset.availableAmount;
      if (isNaN(newAmount) || newAmount <= 0) {
        return;
      }
      setAmount(newAmount.toString());
    } else {
      newAmount = selectedSaleAsset.availableAmount * rate.rate;
      if (isNaN(newAmount) || newAmount <= 0) {
        return;
      }
      setAmount(newAmount.toString());
    }
  };

  const switchConversion = () => {
    const currentAmount = parseFloat(amount || 0);
    const currentConverted = parseFloat(convertedValue || 0);
    const newIsBaseInput = !isBaseInput;

    setIsBaseInput(newIsBaseInput);
    setIsBaseInputParent(newIsBaseInput);

    if (newIsBaseInput) {
      setAmount(currentConverted.toString());
      setConvertedValue(currentAmount || 0);
      if (!tragetAmount && rate?.rate) {
        takeInOutAmount(currentAmount || 0, currentConverted);
      } else if (tragetAmount) {
        takeInOutAmount(currentConverted * tragetAmount, currentConverted);
      }
    } else {
      setAmount(currentConverted.toString());
      setConvertedValue(currentAmount || 0);
      if (!tragetAmount && rate?.rate) {
        takeOutInAmount(currentConverted, currentAmount || 0);
      } else if (tragetAmount) {
        takeOutInAmount(currentConverted, currentConverted / tragetAmount);
      }
    }

    if (!isNaN(currentConverted) && currentConverted > 0) {
      validateAmount(
        currentConverted,
        currentConverted.toString(),
        newIsBaseInput
      );
    }
  };

  const onTargetChange = (event) => {
    const newTarget = event.target.value;

    if (newTarget < rate?.rate) {
      setTargetAmountHolder(newTarget);
      setTragetAmount(null);
      setHighTarget(true);
    }

    if (newTarget >= rate?.rate) {
      setTargetAmountHolder(newTarget);
      setHighTarget(false);
    }
  };

  const handleTargetDone = () => {
    if (targetAmountHolder >= rate?.rate) {
      setTragetAmount(targetAmountHolder);
      setHighTarget(false);
      setOrderOpen(false);
      if (onTarget) {
        onTarget(targetAmountHolder);
      }
    }
  };
  const handleCancel = () => {
    setTragetAmount(null);
    setTargetAmountHolder(null);
    setOrderOpen(false);
  };
  return (
    <>
      {selectedPair && typeOfTrade === "Sell" && (
        <div className="flex justify-center gap-5 bg-gray-50 py-3 rounded-full text-center">
          {loadingRate || (awaitingSaleRate && !tragetAmount) ? (
            <p>
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </p>
          ) : (
            !tragetAmount && (
              <div className="flex justify-center">
                {selectedSaleAsset?.asset.toUpperCase()}/
                {selectedPair?.asset.toUpperCase()} ≈{" "}
                <strong>{Number(rate?.rate ?? 0).toFixed(2)}</strong>
              </div>
            )
          )}

          {tragetAmount && (
            <div className="flex justify-between items-center">
              <div>
                Buy at custom rate {selectedSaleAsset?.asset.toUpperCase()} ≈{" "}
                <strong>{tragetAmount}</strong>
              </div>
            </div>
          )}
          <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
            {loadingRate || awaitingSaleRate ? (
              <p></p>
            ) : (
              selectedPair &&
              !tragetAmount &&
              !isPostTrade && <div className="flex justify-center"></div>
            )}
            {tragetAmount && (
              <div className="flex justify-between gap-2">
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setOrderOpen(false)}
                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                  >
                    <Pencil size={18} className="text-gray-700" />
                  </Button>
                </DialogTrigger>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTragetAmount(null);
                    onTargetCancel?.();
                  }}
                  className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <Trash2 size={18} className="text-gray-700" />
                </Button>
              </div>
            )}
            <DialogContent className="max-w-sm rounded-2xl p-6">
              <Button
                variant="ghost"
                onClick={() => setOrderOpen(false)}
                className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </Button>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Create a sell order
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                {loading ? (
                  <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                    <div className="flex items-center gap-3 ">
                      <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                      <div className="flex flex-col gap-1">
                        <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                      </div>
                    </div>
                    <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                  </div>
                ) : (
                  <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex justify-center items-center">
                    <div className="text-sm  font-thin text-gray-600">
                      {selectedSaleAsset?.asset.toUpperCase()}/
                      {selectedPair?.asset.toUpperCase()} ≈{rate?.rate}
                    </div>
                  </div>
                )}

                <div className="border rounded-lg p-6">
                  <div className="text-md text-gray-700 font-semibold">
                    Sell when the price of 1{" "}
                    {selectedSaleAsset?.asset.toUpperCase()}
                  </div>
                  <div className="relative flex items-center rounded-2xl mt-3">
                    <span className="absolute left-4 text-md font-semibold text-brand-primary">
                      R
                    </span>
                    <Input
                      className="flex-1 text-md font-semibold pl-10 pr-3 py-7 text-brand-primary border rounded-lg"
                      value={targetAmountHolder}
                      onChange={onTargetChange}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="rounded-full py-3 px-[55px] border-gray-300 text-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTargetDone}
                    disabled={highTarget}
                    variant="outline"
                    className="py-3 px-[61px] text-white rounded-full bg-gradient-to-tr from-purple-500 to-rose-500 hover:opacity-90"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {selectedSaleAsset && typeOfTrade === "Sell" && (
        <div className="grid grid-cols-4 gap-4 grid-rows-1 py-8">
          <div className="space-y-2 col-span-1">
            <label className="text-gray-600 text-sm">Sell For</label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                {loading ? (
                  <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                    <div className="flex items-center gap-3 ">
                      <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                      <div className="flex flex-col gap-1">
                        <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                      </div>
                    </div>
                    <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                  </div>
                ) : (
                  <Button
                    disabled={awaitingRate || awaitingSaleRate}
                    variant="outline"
                    className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10  rounded-full flex items-center justify-center">
                        <img
                          src={selectedPair?.assetIcon || coin}
                          alt={selectedPair?.assetName || "Default Asset"}
                          width={32}
                          height={32}
                          className="text-white"
                        />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold">
                          {selectedPair?.asset.toUpperCase() || "Select..."}
                        </div>
                      </div>
                    </div>
                    <ChevronDown className="text-gray-400" />
                  </Button>
                )}
              </DialogTrigger>

              <DialogContent className="max-w-sm rounded-2xl p-6">
                <DialogClose asChild>
                  <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </DialogClose>

                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-gray-800">
                    Select Payment Method
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                  {currentPairs &&
                    currentPairs.map((asset) => {
                      return (
                        <button
                          key={asset.id}
                          onClick={() => {
                            handleSelect(asset);
                          }}
                          className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                        >
                          <img
                            src={asset.assetIcon}
                            alt={asset.assetName}
                            className="w-6 h-6"
                          />
                          <div className="flex flex-col text-left">
                            <span>{asset.assetName}</span>
                            <span className="text-xs text-gray-500">
                              {asset.asset?.toUpperCase()}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2 col-span-3">
            <label className="text-gray-600 text-sm">{"Enter Amount"}</label>
            <div
              className={`flex items-center gap-4 rounded-2xl p-3 border ${
                flag ? "border-2 border-rose-500 " : " "
              }`}
            >
              <input
                type="text"
                value={amount}
                onChange={onTextChange}
                className={`flex-1 text-base md:text-sm font-semibold bg-transparent outline-none 
    focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
    ${flag ? "text-rose-500" : "text-brand-primary"}`}
                placeholder="Enter amount"
              />
              <div className="text-gray-500">
                ≈{" "}
                {convertedValue === " " ||
                isNaN(parseFloat(String(convertedValue)))
                  ? (() => {
                      const asset = isBaseInput
                        ? selectedPair?.asset?.toLowerCase()
                        : selectedSaleAsset?.asset?.toLowerCase();
                      const assetSymbol = isBaseInput
                        ? selectedPair?.asset?.toUpperCase()
                        : selectedSaleAsset?.asset?.toUpperCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt" || asset === "zar";
                      const decimals =
                        assetSymbol === "BTC"
                          ? 6
                          : isTwoDecimalCurrency
                            ? 2
                            : 2;
                      return `0.${"0".repeat(decimals)}`;
                    })()
                  : (() => {
                      const asset = isBaseInput
                        ? selectedPair?.asset?.toLowerCase()
                        : selectedSaleAsset?.asset?.toLowerCase();
                      const assetSymbol = isBaseInput
                        ? selectedPair?.asset?.toUpperCase()
                        : selectedSaleAsset?.asset?.toUpperCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt" || asset === "zar";
                      const decimals =
                        assetSymbol === "BTC"
                          ? 6
                          : isTwoDecimalCurrency
                            ? 2
                            : 2;
                      return truncateDecimals(
                        parseFloat(String(convertedValue)),
                        decimals
                      );
                    })()}{" "}
                {isBaseInput
                  ? selectedPair?.asset.toUpperCase()
                  : selectedSaleAsset?.asset.toUpperCase()}{" "}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={switchConversion}
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
                <Button
                  onClick={maxQuoteAmount}
                  variant="outline"
                  className="rounded-full"
                >
                  Max
                </Button>
              </div>
            </div>
            {flag && (
              <p className="mt-1 text-sm text-rose-500">
                This amount exceeds your available balance
              </p>
            )}
            {flagMinimum && (
              <p className="mt-1 text-sm text-rose-500">
                This amount is too small
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
